import type Device from "../../models/Device";
import { ValueLevels } from "../../models/KnxInstanceData";
import { isUiModeDeveloper, isUiModeExpert, isUiModeProductAdmin } from "../../uiMode";
import { itx } from "../../translationItx/itx";
import KnxPropertyViewAdminButton from "./KnxPropertyViewAdminButton";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";

export interface KnxPropertyIndicatorProps {
  device: Device;
  index: number;
  label: string;
  refId: string;
  instanceId: string;
  moduleId: string;
  isSceneItem: boolean;
  isChannelTypeProperty: boolean;
  defaultValue: string | undefined;
  valueList: Array<{ key: string; value: string; valueEN: string | undefined }>;
  isInCell?: boolean;
}

//
// Wrapper um KnxPropertyViewAdminButton: fügt den Status-Indikator (Punkt + Tooltip)
// als buttonContent hinzu.
//
// Vorher als `KnxPropertyIndicatorIndicator(...)` lokal in KnxParameterViewer definiert.
//
export default function KnxPropertyIndicator(props: KnxPropertyIndicatorProps): any {
  const {
    device,
    index,
    label,
    refId,
    instanceId,
    moduleId,
    isSceneItem,
    isChannelTypeProperty,
    defaultValue,
    valueList,
    isInCell,
  } = props;

  useOnChangeWatcher(device.knxInstanceData);

  const buttonContent = renderIndicator({
    device,
    refId,
    instanceId,
    moduleId,
    isSceneItem,
    isChannelTypeProperty,
    isInCell,
  });

  if (!buttonContent && !isUiModeProductAdmin()) {
    return null;
  }

  return (
    <KnxPropertyViewAdminButton
      device={device}
      instanceId={instanceId}
      id={refId}
      label={label}
      index={index}
      isInCell={isInCell || false}
      defaultValue={defaultValue}
      valueList={valueList}
      buttonContent={buttonContent}
    />
  );
}

interface IndicatorParams {
  device: Device;
  refId: string;
  instanceId: string;
  moduleId: string;
  isSceneItem: boolean;
  isChannelTypeProperty: boolean;
  isInCell?: boolean;
}

export interface KnxPropertyBackgroundColorParams {
  device: Device;
  refId: string;
  instanceId: string;
}

export function calculateKnxIndicationColor({
  device,
  refId,
  instanceId,
}: KnxPropertyBackgroundColorParams): string | undefined {
  if (!isUiModeExpert()) {
    return undefined;
  }

  const knxInstanceData = device.knxInstanceData;
  const level = knxInstanceData.getLevel(refId, instanceId);
  const currentValueExt = knxInstanceData.getExtendedData(refId, instanceId);

  const propertyIsPresetByEstada = level == ValueLevels.estadaPreset;
  const isChannelTypeProperty = !!currentValueExt?.channelTypeProperty;

  if (level == ValueLevels.estadaUi || level == ValueLevels.device) {
    return "#4fe";  // no itx
  }

  if (level == ValueLevels.interactive) {
    return "#0f0"; // no itx
  }
  if (isChannelTypeProperty) {
    return "#00f"; // no itx
  }

  if (propertyIsPresetByEstada) {
    return "darkgrey";  // no itx
  }
  // return "rgb(229, 126, 4)"; // no itx

  return undefined;
}

//
// Status-Indikator (kleiner farbiger Punkt mit Tooltip), zeigt an
// ob die Property von Estada verwaltet/gesetzt/voreingestellt ist,
// vom User bearbeitet wurde, ein Szenenwert oder ein Kanaltyp ist,
// und ob sie in Berechnungen verwaltet wird.
//
// Vorher als `Indicator2(...)` lokal in KnxParameterViewer definiert.
//
function renderIndicator(params: IndicatorParams) {
  const { device, refId, instanceId, moduleId, isSceneItem, isChannelTypeProperty, isInCell } = params;
  const knxInstanceData = device.knxInstanceData;

  const currentValueExt = knxInstanceData.getExtendedData(refId, instanceId);
  const propertyIsManagedByEstada = currentValueExt?.mbe;
  const propertyIsSetByEstada = currentValueExt?.mbe?.isSet;

  const level = knxInstanceData.getLevel(refId, instanceId);

  const propertyIsSetByUser = level == ValueLevels.interactive;
  const propertyIsPresetByEstada = level == ValueLevels.estadaPreset;

  const isPClacProperty =
    isUiModeDeveloper() && knxInstanceData.paramsToCalculationReferences.get(moduleId + refId)?.size;

  let tooltipHtml = "";

  const backgroundColor = calculateKnxIndicationColor({
    device,
    refId,
    instanceId,
  });

  if (propertyIsPresetByEstada) {
    tooltipHtml = itx(
      "knxParameterViewer.propertyPresetFromESTADA",
      "Diese Property wurde durch Estada voreingestellt ",
    );
  }

  if (propertyIsManagedByEstada) {
    tooltipHtml = itx("knxParameterViewer.propertyManagedFromESTADA", "Diese Property wird von Estada verwaltet");
  }

  if (isSceneItem) {
    tooltipHtml = state.translate(
      "knxParameterViewer.propertyRecognizedAsScene",
      `Diese Property wurde als Szenenwert erkannt`,
    );
  }

  if (isChannelTypeProperty) {
    tooltipHtml = state.translate(
      "knxParameterViewer.propertyRecognizedAsChannelType",
      `Diese Property wurde als Kanaltyp erkannt`,
    );
  }

  if (propertyIsSetByEstada) {
    if (tooltipHtml) {
      tooltipHtml += "<br/>";
    }
    tooltipHtml = state.translate(
      "knxParameterViewer.propertySetAutomatically",
      `Diese Property wird von Estada automatisch gesetzt`,
    );
  }

  if (propertyIsSetByUser) {
    if (tooltipHtml) {
      tooltipHtml += "<br/>";
    }
    tooltipHtml = itx("knxParameterViewer.propertyEdited", "Diese Property wurde bearbeitet");
  }

  if (isUiModeProductAdmin()) {
    tooltipHtml += `<br/>id= ${refId}`; // no itx
    if (instanceId) {
      tooltipHtml += `<br/>instanceId= ${instanceId}`; // no itx
    }
  }

  let content = "";

  if (isPClacProperty) {
    tooltipHtml += "<br />" + "Diese Property wird in Berechnungen verwaltet";
    content = "C";
  }

  if (
    isUiModeProductAdmin() ||
    propertyIsManagedByEstada ||
    propertyIsSetByUser ||
    propertyIsPresetByEstada ||
    isChannelTypeProperty ||
    isPClacProperty
  ) {
    return (
      <div
        // className={"knxIndicator" + (isInCell ? " small" : "")}
        style={{
          backgroundColor: backgroundColor,
        }}
      >
        <div
          key={`${propertyIsManagedByEstada} ${propertyIsSetByUser} ${propertyIsPresetByEstada} ${isChannelTypeProperty}`}
          // style={{
          //   position: "absolute",
          //   width: 300
          //   display: "flex",
          //   flexDirection: "row",
          //   alignItems: "center",
          //   justifyContent: "center",
          //   paddingBottom: "2px",
          //   paddingRight: "1px",
          //   color: "grey",
          //   height: "20px",
          //   width: "20px",
          //   borderRadius: "10px",
          //   backgroundColor: backgroundColor,
          //   overflow: "hidden",
          // }}
          data-tooltip-id="tv-tooltip"
          data-tooltip-html={tooltipHtml}
          data-tooltip-place="top-start" // no itx
          data-tooltip-variant="info" // no itx
        >
          {content}
        </div>
      </div>
    );
  } else {
    return null;
  }
}
